<!-- ✅ DealerVatReportForm.vue (FULL UPDATED - uses fetchDealerListManagement) -->
<template>
  <div class="container">
    <div class="dropdown-container">
      <!-- Distributor -->
      <div class="dropdown-item">
        <label>Select Distributor</label>
        <select id="distributorSelect" v-model="selectedDistributorId" @change="onDistributorChange"
          :disabled="isDistributorLocked || isBusy">
          <option value="">Select Distributor</option>

          <option v-if="isAsmUser || isFsmUser || isOtherUser" :value="ALL">
            All Distributors
          </option>

          <option v-for="d in distributorList" :key="d.profile_id ?? d.id" :value="String(d.profile_id ?? d.id)">
            {{ d.business_name || d.business_display_name || d.name || ("Distributor #" + (d.profile_id ?? d.id)) }}
          </option>
        </select>
      </div>

      <!-- Dealer -->
      <div class="dropdown-item">
        <label>Select Dealer</label>
        <select id="dealerSelect" v-model="selectedDealerId"
          :disabled="isBusy || !selectedDistributorId || selectedDistributorId === ALL">
          <option value="">Select Dealer</option>

          <!-- All Dealers option (only if real distributor selected + dealer list has rows) -->
          <option v-if="selectedDistributorId && selectedDistributorId !== ALL && dealerList.length" :value="ALL">
            All Dealers
          </option>

          <!-- ✅ IMPORTANT: value must be dealer PROFILE id (matches invoices.dealer_id) -->
          <option v-for="d in dealerList" :key="dealerProfileIdOf(d)" :value="String(dealerProfileIdOf(d))">
            {{ dealerLabelOf(d) }}
          </option>
        </select>
      </div>

      <!-- Start Date -->
      <div class="dropdown-item">
        <label>Start Date</label>
        <input type="date" v-model="startDate" :max="currentDate" @change="handleDateChange"
          @blur="validateStartDate(true)" />
      </div>

      <!-- End Date -->
      <div class="dropdown-item">
        <label>End Date</label>
        <input type="date" v-model="endDate" :max="currentDate" @change="handleDateChange"
          @blur="validateEndDate(true)" />
      </div>

      <!-- Search -->
      <div class="dropdown-item search-container">
        <button @click="performSearch" class="btn button-default mt-4" :disabled="isBusy">
          {{ loadingReport ? "Searching..." : "Search" }}
        </button>
      </div>

      <!-- Export -->
      <div class="dropdown-item search-container">
        <button @click="exportToExcel" class="btn button-export mt-4" :disabled="!reportData.length">
          Export to Excel
        </button>
      </div>
    </div>

    <!-- Loading -->
    <div v-if="isBusy" class="loading-spinner mt-3">
      <i class="fa fa-spinner fa-spin"></i>
      <span v-if="loadingInit"> Loading...</span>
      <span v-else-if="loadingDealers"> Loading dealers...</span>
      <span v-else-if="loadingReport"> Loading report...</span>
      <span v-else> Loading...</span>
    </div>

    <!-- Table -->
    <div v-if="!isBusy && reportData.length" class="card mt-3">
      <div class="table-scroll">
        <table class="table table-striped table-bordered">
          <thead>
            <tr>
              <th>Serial No</th>
              <th>Invoice Date</th>
              <th>Tax Invoice No</th>
              <th>Dealer's TIN</th>
              <th>Dealer Name</th>
              <th class="text-end">Net Total</th>
              <th class="text-end">Gross Total</th>
              <th class="text-end">VAT Amount</th>
            </tr>
          </thead>

          <tbody>
            <tr v-for="(row, i) in reportData" :key="i">
              <td>{{ row.serial_no ?? (i + 1) }}</td>
              <td>{{ row.invoice_date || "-" }}</td>
              <td>{{ row.tax_invoice_no || "-" }}</td>
              <td>{{ row.dealer_tin || "-" }}</td>
              <td>{{ row.dealer_name || "-" }}</td>
              <td class="text-end">{{ formatNumber(row.net_total) }}</td>
              <td class="text-end">{{ formatNumber(row.gross_total) }}</td>
              <td class="text-end">{{ formatNumber(row.vat_amount) }}</td>
            </tr>
          </tbody>
        </table>
      </div>
    </div>

    <div v-if="!isBusy && searchedOnce && !reportData.length" class="no-data-message">
      <p>No data found. Please select filters and click Search.</p>
    </div>
  </div>
</template>

<script>
import * as XLSX from "xlsx";
import { useToast } from "vue-toastification";

import {
  fetchDistributors,
  fetchDealerListManagement, // ✅ use supervisor requested API
  fetchDealerVatReport,
} from "@/services/dataService";

export default {
  name: "DealerVatReportForm",

  data() {
    const currentDate = new Date().toISOString().split("T")[0];
    const ALL = "__all__";

    return {
      token: localStorage.getItem("token_set") || "",
      profileId: Number(localStorage.getItem("profile_id") || 0),
      myProfileId: Number(localStorage.getItem("profile_id") || 0),

      currentDate,
      startDate: currentDate,
      endDate: currentDate,

      loadingInit: false,
      loadingDealers: false,
      loadingReport: false,

      searchedOnce: false,

      userLabel: "",
      roleKey: "OTHER",

      distributorList: [],
      selectedDistributorId: "",
      isDistributorLocked: false,

      dealerList: [],
      selectedDealerId: "",

      reportData: [],

      ALL,
      redirecting: false,
    };
  },

  computed: {
    isDistributorUser() { return this.roleKey === "DIS"; },
    isAsmUser() { return this.roleKey === "ASM"; },
    isFsmUser() { return this.roleKey === "FSM"; },
    isOtherUser() { return this.roleKey === "OTHER"; },

    // ✅ one busy flag for UI
    isBusy() {
      return this.loadingInit || this.loadingDealers || this.loadingReport;
    },
  },

  mounted() {
    this.init();
  },

  methods: {
    toast(type, message) {
      const t = useToast();
      type === "error"
        ? t.error(message, { position: "top-right", timeout: 3000 })
        : t.success(message, { position: "top-right", timeout: 3000 });
    },

    // ===== auth helpers =====
    bodyOf(x) { return x?.data ?? x; },
    msgOf(x) {
      const b = this.bodyOf(x?.response?.data ?? x);
      return String(b?.message ?? b?.error ?? x?.message ?? "");
    },

    isUnauthorizedAny(x) {
      const http = x?.response?.status ?? x?.status;
      const msg = this.msgOf(x).toLowerCase();
      const authMsg =
        /access token is invalid|unauthenticated|token.*expired|expired token|invalid token|session.*expired/;

      if (http === 401) return authMsg.test(msg);
      return http === 419 || http === 440 || http === 498 || authMsg.test(msg);
    },

    handleUnauthorized(x) {
      if (!this.isUnauthorizedAny(x)) return false;
      this.redirectToLogin();
      return true;
    },

    redirectToLogin() {
      if (this.redirecting) return;
      this.redirecting = true;

      this.toast("error", "Session expired. Please login again.");

      localStorage.removeItem("token_set");
      localStorage.removeItem("profile_id");
      localStorage.removeItem("user_label");
      localStorage.removeItem("userLabel");
      localStorage.removeItem("role");

      if (this.$router) this.$router.replace("/login");
      else window.location.replace("/login");
    },

    // ✅ active user filter (optional)
    isActiveUser(x) {
      const v = x?.is_active ?? x?.user_is_active ?? x?.app_user_is_active ?? x?.active;
      if (v === undefined || v === null) return true;
      return v === true || Number(v) === 1;
    },
    onlyActive(list) {
      return (Array.isArray(list) ? list : []).filter(this.isActiveUser);
    },

    initRoleFromStorage() {
      const label =
        localStorage.getItem("user_label") ||
        localStorage.getItem("userLabel") ||
        localStorage.getItem("role") ||
        "";

      this.userLabel = String(label);
      const up = this.userLabel.toUpperCase().trim();

      if (up === "DISTRIBUTOR" || up.startsWith("DISTRIBUTOR") || up === "DIS" || up.startsWith("DIS_")) this.roleKey = "DIS";
      else if (up === "ASM" || up.startsWith("ASM_")) this.roleKey = "ASM";
      else if (up === "FSM" || up.startsWith("FSM_")) this.roleKey = "FSM";
      else this.roleKey = "OTHER";
    },

    // ===== dates =====
    isValidISODate(dateStr) {
      if (!dateStr || typeof dateStr !== "string") return false;
      if (dateStr.includes("/")) return false;
      const m = dateStr.match(/^(\d{4})-(\d{2})-(\d{2})$/);
      if (!m) return false;
      const y = Number(m[1]), mo = Number(m[2]), d = Number(m[3]);
      const dt = new Date(y, mo - 1, d);
      return dt.getFullYear() === y && dt.getMonth() === mo - 1 && dt.getDate() === d;
    },

    validateStartDate(showToast = true) {
      if (!this.startDate) return true;
      if (!this.isValidISODate(this.startDate)) {
        if (showToast) this.toast("error", "Invalid Start Date");
        this.startDate = "";
        return false;
      }
      if (this.startDate > this.currentDate) {
        if (showToast) this.toast("error", "Start Date cannot be in the future");
        this.startDate = "";
        return false;
      }
      return true;
    },

    validateEndDate(showToast = true) {
      if (!this.endDate) return true;
      if (!this.isValidISODate(this.endDate)) {
        if (showToast) this.toast("error", "Invalid End Date");
        this.endDate = "";
        return false;
      }
      if (this.endDate > this.currentDate) {
        if (showToast) this.toast("error", "End Date cannot be in the future");
        this.endDate = "";
        return false;
      }
      return true;
    },

    handleDateChange() {
      this.validateStartDate(false);
      this.validateEndDate(false);
      if (this.startDate && this.endDate && this.endDate < this.startDate) {
        this.toast("error", "End date cannot be before start date.");
        this.endDate = "";
      }
    },

    validateDatesForSearch() {
      if (!this.startDate) { this.toast("error", "Please select valid Start Date"); return false; }
      if (!this.endDate) { this.toast("error", "Please select valid End Date"); return false; }
      if (!this.validateStartDate(true)) return false;
      if (!this.validateEndDate(true)) return false;
      if (this.endDate < this.startDate) { this.toast("error", "End date cannot be before start date."); return false; }
      return true;
    },

    validateFiltersOneByOne() {
      if (!this.selectedDistributorId) {
        this.toast("error", "Please select Distributor.");
        return false;
      }
      if (this.selectedDistributorId !== this.ALL && !this.selectedDealerId) {
        this.toast("error", "Please select Dealer.");
        return false;
      }
      return true;
    },

    // ===== format =====
    formatNumber(v) {
      const n = Number(v);
      if (!Number.isFinite(n)) return "0.00";
      return n.toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 });
    },

    // ✅ dealer PROFILE id mapping (must match invoices.dealer_id)
    dealerProfileIdOf(d) {
      return (
        d?.profile_id ??
        d?.dealer_profile_id ??
        d?.dealer_id ??
        d?.profileId ??
        d?.id ??
        null
      );
    },
    dealerLabelOf(d) {
      return (
        d?.business_display_name ||
        d?.business_name ||
        d?.name ||
        `Dealer #${this.dealerProfileIdOf(d) ?? ""}`
      );
    },

    // ===== init/load =====
    async init() {
      this.loadingInit = true;
      try {
        this.token = localStorage.getItem("token_set") || "";
        if (!this.token) { this.redirectToLogin(); return; }

        this.profileId = Number(localStorage.getItem("profile_id") || 0);
        this.myProfileId = this.profileId;

        this.initRoleFromStorage();

        await this.loadDistributors();
        if (this.redirecting) return;
      } catch (e) {
        if (this.handleUnauthorized(e)) return;
      } finally {
        this.loadingInit = false;
      }
    },

    async loadDistributors() {
      try {
        const res = await fetchDistributors(this.profileId, this.token);
        if (this.handleUnauthorized(res)) return;

        const body = this.bodyOf(res);
        const listRaw = body?.data || body || [];
        this.distributorList = this.onlyActive(listRaw);

        if (this.isDistributorUser && this.myProfileId) {
          this.selectedDistributorId = String(this.distributorList[0]?.profile_id);
          this.isDistributorLocked = true;
          await this.onDistributorChange();
        } else {
          this.selectedDistributorId = "";
          this.isDistributorLocked = false;
        }
      } catch (err) {
        if (this.handleUnauthorized(err)) return;
        console.error(err?.response?.data || err);
        this.toast("error", "Failed to load distributor list.");
      }
    },

    async onDistributorChange() {
      this.selectedDealerId = "";
      this.dealerList = [];
      this.reportData = [];
      this.searchedOnce = false;

      if (!this.selectedDistributorId) return;
      if (this.selectedDistributorId === this.ALL) return;

      await this.loadDealers();
    },

    // ✅ uses supervisor requested API
    async loadDealers() {
      this.loadingDealers = true;
      try {
        const distId = Number(this.selectedDistributorId);
        if (!distId) return;

        // fetchDealerListManagement(profileId, distributorId, token)
        const res = await fetchDealerListManagement(this.profileId, distId, this.token);
        if (this.handleUnauthorized(res)) return;

        const body = this.bodyOf(res);

        const list =
          (Array.isArray(body?.data) && body.data) ||
          (Array.isArray(body?.dealers) && body.dealers) ||
          (Array.isArray(body) && body) ||
          [];

        this.dealerList = list
          .map(d => ({ ...d, profile_id: this.dealerProfileIdOf(d) }))
          .filter(d => d.profile_id);

        console.log("[DealerListManagement] dist:", distId, "dealers:", this.dealerList);
      } catch (err) {
        if (this.handleUnauthorized(err)) return;
        console.error(err?.response?.data || err);
        this.toast("error", "Failed to load dealers for selected distributor.");
      } finally {
        this.loadingDealers = false;
      }
    },

    // ===== search =====
    async performSearch() {
      this.token = localStorage.getItem("token_set") || "";
      if (!this.token) return this.redirectToLogin();

      if (!this.validateFiltersOneByOne()) return;
      if (!this.validateDatesForSearch()) { this.searchedOnce = true; return; }

      this.loadingReport = true;
      this.searchedOnce = false;
      this.reportData = [];

      try {
        const payload = {
          start_date: this.startDate,
          end_date: this.endDate,
          distributor_profile_id: this.selectedDistributorId === this.ALL ? null : Number(this.selectedDistributorId),
          dealer_profile_id:
            (this.selectedDistributorId === this.ALL || this.selectedDealerId === this.ALL)
              ? null
              : Number(this.selectedDealerId),
        };

        console.log("Dealer VAT payload:", payload);

        const apiRes = await fetchDealerVatReport(this.token, payload);
        if (this.handleUnauthorized(apiRes)) return;

        const res = this.bodyOf(apiRes);

        if (!res || res.status !== true) {
          this.toast("error", res?.message || "Error loading Dealer VAT Report.");
          return;
        }

        this.reportData = Array.isArray(res.data) ? res.data : [];

        // ✅ FIX: don't show "Dealer VAT Report" as error message
        if (!this.reportData.length) {
          this.toast("error", "No data found for selected filters.");
          return;
        }

        this.toast("success", "Report Generated successfully.");
      } catch (err) {
        if (this.handleUnauthorized(err)) return;

        const msg =
          err?.response?.data?.message ||
          err?.response?.data?.error ||
          err?.message ||
          "Error loading report.";

        console.error("[DealerVAT] error:", err?.response?.data || err);
        this.toast("error", msg);
      } finally {
        this.loadingReport = false;
        this.searchedOnce = true;
      }
    },

    // ===== excel export =====
    exportToExcel() {
      if (!this.reportData.length) {
        this.toast("error", "No data to export.");
        return;
      }

      const fullCols = 8;
      const padTo = (arr) => {
        const out = Array.isArray(arr) ? [...arr] : [];
        while (out.length < fullCols) out.push("");
        return out.slice(0, fullCols);
      };

      // ---- FILTERS (Top Metadata) ----
      let distributorName = "All Distributors";
      if (this.selectedDistributorId && this.selectedDistributorId !== this.ALL) {
        const selectedDist = this.distributorList.find(
          d => String(d.profile_id ?? d.id) === String(this.selectedDistributorId)
        );
        if (selectedDist) {
          distributorName =
            selectedDist.business_name ||
            selectedDist.business_display_name ||
            selectedDist.name ||
            `Distributor #${this.selectedDistributorId}`;
        }
      }

      let dealerName = "All Dealers";
      if (this.selectedDealerId && this.selectedDealerId !== this.ALL) {
        const selectedDealer = this.dealerList.find(
          d => String(this.dealerProfileIdOf(d)) === String(this.selectedDealerId)
        );
        if (selectedDealer) {
          dealerName = this.dealerLabelOf(selectedDealer);
        }
      }

      const filterRows = [
        ["Dealer VAT Report"],
        [],
        ["Distributor", distributorName],
        ["Dealer",      dealerName],
        ["Start Date",  this.startDate  || "-"],
        ["End Date",    this.endDate    || "-"],
        [],
      ];

      // ---- HEADER ----
      const headerRow = [
        "Serial No",
        "Invoice Date",
        "Tax Invoice No",
        "Dealer's TIN",
        "Dealer Name",
        "Net Total",
        "Gross Total",
        "VAT Amount",
      ];

      // ---- DATA ROWS ----
      const dataRows = this.reportData.map((r, idx) => ([
        r.serial_no ?? (idx + 1),
        r.invoice_date  || "",
        r.tax_invoice_no || "",
        r.dealer_tin    || "",
        r.dealer_name   || "",
        Number(r.net_total)   || 0,
        Number(r.gross_total) || 0,
        Number(r.vat_amount)  || 0,
      ]));

      // ---- FOOTER (Bottom Metadata) ----
      const firstName  = localStorage.getItem("first_name") || "";
      const lastName   = localStorage.getItem("last_name")  || "";
      const userFullName = (firstName + " " + lastName).trim() || "-";
      const generatedAt  = new Date().toLocaleString("en-US", { hour12: false });

      const footerRows = [
        [],
        ["Generated by", userFullName],
        ["Generated at", generatedAt],
      ];

      // ---- BUILD FINAL AOA ----
      const aoa = [
        ...filterRows.map(padTo),
        padTo(headerRow),
        ...dataRows.map(padTo),
        ...footerRows.map(padTo),
      ];

      const ws = XLSX.utils.aoa_to_sheet(aoa);

      // ---- MERGES ----
      const merges = [];
      // Merge title row across first 4 cols
      merges.push({ s: { r: 0, c: 0 }, e: { r: 0, c: 3 } });
      ws["!merges"] = merges;

      // Make title bold
      if (ws["A1"]) ws["A1"].s = { font: { bold: true, sz: 14 } };

      const wb = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(wb, ws, "Dealer_VAT_Report");
      XLSX.writeFile(wb, `Dealer_VAT_Report_${this.startDate}_to_${this.endDate}.xlsx`);
      this.toast("success", "Excel exported successfully.");
    },
  },
};
</script>

<style scoped>
.table-scroll {
  max-height: 500px;
  overflow: auto;
  border: 1px solid #ddd;
  margin-top: 10px;
  position: relative;
}

.table thead th {
  position: sticky;
  top: 0;
  background: #f9f9f9;
  z-index: 5;
  white-space: nowrap;
}

.container {
  background-color: #ffffff;
  box-shadow: 0 0 15px rgba(0, 0, 0, 0.1);
  border-radius: 8px;
  padding: 20px;
  margin: 10px;
  width: calc(100% - 20px);
  box-sizing: border-box;
}

.dropdown-container {
  display: grid;
  grid-template-columns: repeat(4, 1fr);
  gap: 15px;
  width: 100%;
  margin-bottom: 20px;
}

.dropdown-item {
  min-width: 180px;
  max-width: 250px;
}

label {
  display: block;
  margin-bottom: 5px;
  font-weight: bold;
  font-size: 14px;
}

select,
input[type="date"] {
  width: 100%;
  padding: 10px;
  border-radius: 5px;
  border: 1px solid #ccc;
  font-size: 14px;
}

.search-container {
  display: flex;
  align-items: flex-end;
  justify-content: left;
}

.btn.button-default {
  padding: 10px 20px;
  border: none;
  border-radius: 5px;
  background-color: #007bff;
  color: #fff;
}

.btn.button-export {
  padding: 10px 20px;
  border: none;
  border-radius: 5px;
  background-color: #28a745;
  color: #fff;
}

.btn:disabled {
  background-color: #6c757d;
  cursor: not-allowed;
}

.loading-spinner {
  font-size: 18px;
  text-align: center;
  margin-top: 20px;
  color: #007bff;
}

.no-data-message {
  text-align: center;
  padding: 40px;
  font-size: 16px;
  color: #666;
}

.text-end {
  text-align: right;
}
</style>
